import { Injectable } from '@angular/core';

import { AnalysResultat, AnalysResultatDetailsDTO, AnalysResultatDTO } from '../models/analys-resultat';
import { NameType } from '../../shared/models/models';
import { IgnoreringDTO } from '../models/ignore';
import { ParkeringDTO } from '../models/parkera';
import { map, Subject } from 'rxjs';
import { Oversikt } from '../../shared/models/manuell-atgard';
import { NyttStandardNamnDTO } from '../../shared/models/standard-namn';
import { SynonymDTO } from '../../shared/models/synonymer';
import { ApiService } from 'src/app/api/api.service';

@Injectable({
  providedIn: 'root',
})
export class AtgardsListaService {
  url = 'analysresultat/actionlist';
  urlOverviewV2 = 'analysresultat/actionlist/overview/v2';
  urlDetails = 'analysresultat/';
  public refetchAtgarder = new Subject<void>();
  constructor(private apiService: ApiService) {}


  getAtgarder() {
    return this.apiService.getAsObservable<Oversikt[]>(`${this.urlOverviewV2}`).pipe(
      map((data: Oversikt[]) => {
        const rows: any[] = [];
        data.forEach((row: Oversikt, index) => {
          rows.push({
            ...row,
            num: row.num,
            provId: row.provId,
            rowidentifier: row.type + row.provId + index,
            icons: [
              row.parked
                ? {
                    displayIcon: row.parked,
                    icon: 'fas fa-circle-pause',
                    customTitle: row.parking_comments?.length > 0 ? row.parking_comments[0].kommentar : null,
                  }
                : null,
            ],
            prov: row.prov,
          });
        });
        return rows;
      }),
    );
  }

  getDetails<T>(row: any) {
    return this.apiService.getAsObservable<T>(`${this.urlDetails}/${row.id}`);
  }

  async checkDuplicate(synonym: any) {
    return await this.apiService.post<boolean>(`${this.url}/synonym/check/duplicates`, synonym);
  }

  /*
   * TODO - Tas bort när tjänsten för grunddata är på plats. Då kan kön skickas med resultatlistan direkt.
   */
  async getGender(id: any): Promise<string> {
    try {
      const url = this.urlDetails + id;
      const data: AnalysResultatDetailsDTO = await this.apiService.get<AnalysResultatDetailsDTO>(url);
      return data.gender || '';
    } catch (e) {
      console.error('Error occurred while fetching gender:', e);
      return ''; // Return empty string on error or handle the error as needed
    }
  }

  synonymisera(synonymDTO: SynonymDTO, nameType: NameType) {
    return this.apiService.post<any>(`${this.url}/synonym/${this.mapNameTypeToUrl(nameType)}`, synonymDTO);
  }

  ignorera(ignoreDTO: IgnoreringDTO, nameType: NameType) {
    return this.apiService.post<any>(`${this.url}/ignore/${this.mapNameTypeToUrl(nameType)}`, ignoreDTO);
  }

  parkera(parkDTO: ParkeringDTO, nameType: NameType) {
    parkDTO.nameType = nameType;
    return this.apiService.post<any>(`${this.url}/parking/`, parkDTO);
  }

  nyttStandardNamn(standardNameDTO: NyttStandardNamnDTO, nameType: NameType) {
    console.log(this.mapStandardNameDTO(standardNameDTO, nameType));
    return this.apiService.post<any>(`${this.url}/standardname/${this.mapNameTypeToUrl(nameType)}`, standardNameDTO);
  }

  mapStandardNameDTO(dto: NyttStandardNamnDTO, nameType: NameType) {
    switch (nameType) {
      case NameType.Smittamne:
        return {
          id: dto.id,
          name: dto.name,
          type: dto.type,
          category: dto.category,
          comments: dto.comments ?? '',
        };
      case NameType.Analys:
        return {
          name: dto.name,
          comments: dto.comments,
          start: dto.start,
          end: dto.end,
        };
      case NameType.Provmaterial:
        return {
          id: dto.snomed_ct_concept_id,
          name: dto.name,
          shortName: dto.shortName,
          term: dto.term,
          comments: dto.comments,
        };
      case NameType.Antibiotika:
        return {
          id: dto.id,
          name: dto.name,
          shortName: dto.shortName,
          comments: dto.comments,
        };
      default:
        return dto;
    }
  }

  addComment(parkDTO: ParkeringDTO) {
    return this.parkera(parkDTO, parkDTO.nameType);
  }

  mapNameTypeToUrl(nameType: NameType): string | undefined {
    switch (nameType) {
      case NameType.Analys:
        return 'ANALYS';
      case NameType.Smittamne:
        return 'SMITTAMNE';
      case NameType.Provmaterial:
        return 'PROVMATERIAL';
      case NameType.Antibiotika:
        return 'ANTIBIOTIKUM';
      case NameType.NegativtFynd:
        return 'NEGATIVT';
      case NameType.BeskrivandeFynd:
        return 'BESKRIVANDE';
      default:
        return undefined;
    }
  }
}
